import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ShoppingBag,
  Sparkles,
  RefreshCw,
  BadgeCheck,
  Percent,
  Play,
  Pause,
  Wrench,
  Tent,
  Home as HomeIcon,
  Cpu,
  Music2,
  Hammer,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import { EBAY_URL } from "@/components/Header";
import useSeo from "@/hooks/useSeo";
import {
  OPENING_HOURS_DISPLAY,
  getOpenStatus,
} from "@/constants/openingHours";
import EbayProducts from "@/components/EbayProducts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  {
    slug: "baumarkt",
    title: "Baumarkt, Garten, Elektro & Smart",
    short: "Baumarkt · Garten",
    image:
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1100&q=80",
    icon: Wrench,
    span: "lg:col-span-3 lg:row-span-2",
  },
  {
    slug: "camping",
    title: "Camping, Freizeit & Outdoor",
    short: "Camping · Outdoor",
    image:
      "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjYW1waW5nJTIwdGVudHxlbnwwfHx8fDE3ODAzODQ3MzN8MA&ixlib=rb-4.1.0&q=85",
    icon: Tent,
    span: "lg:col-span-3 lg:row-span-1",
  },
  {
    slug: "haushalt",
    title: "Haushaltsgeräte, Küche & Wohnen",
    short: "Haushalt · Wohnen",
    image:
      "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBraXRjaGVuJTIwYXBwbGlhbmNlc3xlbnwwfHx8fDE3ODAzODQ3MzN8MA&ixlib=rb-4.1.0&q=85",
    icon: HomeIcon,
    span: "lg:col-span-3 lg:row-span-1",
  },
  {
    slug: "it",
    title: "PC, IT, Büro, Drucker, Uhren & Handys",
    short: "PC · IT · Büro",
    image:
      "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?auto=format&fit=crop&w=1100&q=80",
    icon: Cpu,
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    slug: "multimedia",
    title: "Multimedia, Audio, Video, HiFi & DAB",
    short: "Multimedia · HiFi",
    image:
      "https://images.unsplash.com/photo-1545454675-3531b543be5d?auto=format&fit=crop&w=1100&q=80",
    icon: Music2,
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    slug: "werkzeuge",
    title: "Heimwerken & Werkzeuge",
    short: "Heimwerk · Werkzeug",
    image:
      "https://images.unsplash.com/photo-1581244277943-fe4a9c777189?auto=format&fit=crop&w=1100&q=80",
    icon: Hammer,
    span: "lg:col-span-2 lg:row-span-2",
  },
];

const MARQUEE_ITEMS = [
  "Markenware",
  "Bis 50% reduziert",
  "Sonderposten",
  "Überschusswaren",
  "Lagerrestbestände",
];

// ---------- Hero ----------
const Hero = () => (
  <section id="top" className="border-b border-[var(--pz-border)]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-24 pb-14 sm:pb-24">
      <div className="grid lg:grid-cols-12 gap-10 items-stretch">
        <div className="lg:col-span-7 pz-fade-up flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="pz-tag pz-tag-gold">
                <Sparkles size={12} /> Sonderposten · Restposten
              </span>
              <span className="pz-tag">
                Einzelhandel · Schenklengsfeld
              </span>
            </div>

            <h1
              data-testid={TESTIDS.heroHeadline}
              className="font-display text-[2.7rem] sm:text-6xl lg:text-7xl font-black leading-[0.98] tracking-tight"
            >
              Markenware.
              <br />
              Bis <span className="pz-highlight font-black">50%</span> reduziert.
            </h1>

            <p className="mt-7 text-base sm:text-lg text-[var(--pz-text-muted)] max-w-xl leading-relaxed">
              Im Prozenthaus findet jeder ein immer wechselndes Angebot aus
              Lagerrestbeständen, Überschusswaren und Sonderposten – aus den
              Bereichen Baumarkt, Haushalt, IT, Multimedia, Camping und
              Heimwerken. Hochwertige Marken, scharf kalkulierte Preise.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={EBAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TESTIDS.heroCtaEbay}
                className="pz-btn pz-btn-primary"
              >
                Im eBay-Shop stöbern <ArrowUpRight size={18} />
              </a>
              <button
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid={TESTIDS.heroCtaContact}
                className="pz-btn pz-btn-ghost"
              >
                Kontakt aufnehmen <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-4 sm:gap-8 border-t border-[var(--pz-border)] pt-8">
            <div>
              <div className="pz-num">-50<span className="text-[var(--pz-accent)]">%</span></div>
              <div className="pz-eyebrow mt-2">Bis zu Rabatt</div>
            </div>
            <div>
              <div className="pz-num">6</div>
              <div className="pz-eyebrow mt-2">Warenbereiche</div>
            </div>
            <div>
              <div className="pz-num font-serif-italic">∞</div>
              <div className="pz-eyebrow mt-2">Wechselndes Sortiment</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 grid grid-rows-5 gap-4 min-h-[460px]">
          <div className="row-span-4 relative overflow-hidden border border-[var(--pz-border)]">
            <img
              src="/hero-warehouse.jpg"
              alt="Prozenthaus Lagerhalle in Schenklengsfeld – Markenware, Sonderposten und Überschusswaren bis 50 % reduziert"
              width="1306"
              height="1087"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-white/95 backdrop-blur-sm px-4 py-3 border border-[var(--pz-border)]">
              <div>
                <div className="pz-eyebrow-gold">Live im Shop</div>
                <div className="font-display font-bold text-sm mt-1">
                  Frisch eingetroffene Sonderposten
                </div>
              </div>
              <a
                href={EBAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pz-btn pz-btn-dark text-xs px-3 py-2"
              >
                Ansehen <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          <div className="row-span-1 bg-[var(--pz-text)] text-white flex items-center justify-between px-6">
            <div className="pz-eyebrow text-white/70">100% Markenware</div>
            <BadgeCheck className="text-[var(--pz-accent)]" />
          </div>
        </div>
      </div>
    </div>

    {/* Static USP strip */}
    <div className="bg-[var(--pz-text)] text-white border-y border-[var(--pz-text)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-7 sm:py-9">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:gap-x-10">
          {MARQUEE_ITEMS.map((item, idx) => (
            <React.Fragment key={item}>
              <span className="font-display font-bold text-lg sm:text-xl tracking-tight whitespace-nowrap">
                {item}
              </span>
              {idx < MARQUEE_ITEMS.length - 1 && (
                <span
                  aria-hidden="true"
                  className="text-[var(--pz-accent)] text-xs"
                >
                  ◆
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- About ----------
const About = () => (
  <section
    id="about"
    data-testid={TESTIDS.aboutSection}
    className="border-b border-[var(--pz-border)]"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="pz-eyebrow-gold mb-4">Was wir machen</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
            Wir verwandeln <span className="pz-highlight">Überschuss</span> in
            Schnäppchen.
          </h2>
          <p className="mt-6 text-[var(--pz-text-muted)] text-lg leading-relaxed">
            Prozenthaus ist ein Einzelhandel, spezialisiert auf Lagerrestbestände,
            Überschusswaren und Sonderposten. Wir kaufen direkt bei Herstellern
            und Großhändlern – und geben den Preisvorteil 1:1 an Sie weiter.
          </p>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-3 gap-px bg-[var(--pz-border)] border border-[var(--pz-border)]">
          {[
            {
              t: "Lagerrestbestände",
              d: "Letzte Stücke aus Herstellerlagern – sofort lieferbar, oft nur einmal verfügbar.",
            },
            {
              t: "Überschusswaren",
              d: "Produktionsüberhänge namhafter Marken zu stark reduzierten Preisen.",
            },
            {
              t: "Sonderposten",
              d: "Auslaufmodelle, B-Ware und Saisonware – geprüfte Qualität, unschlagbar günstig.",
            },
          ].map((b, i) => (
            <div key={i} className="bg-white p-8">
              <div className="font-display font-black text-3xl text-[var(--pz-accent)]">
                0{i + 1}
              </div>
              <div className="mt-4 font-display font-bold text-xl">{b.t}</div>
              <p className="mt-2 text-sm text-[var(--pz-text-muted)] leading-relaxed">
                {b.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- Video Showcase ----------
const VideoShowcase = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      id="erlebnis"
      data-testid="video-showcase-section"
      className="border-b border-[var(--pz-border)] bg-[var(--pz-text)] text-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="pz-eyebrow-gold mb-4">Einblick</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
              Sortiment
              <br />
              in <span className="font-serif-italic font-medium text-[var(--pz-accent-light)]">Bewegung</span>.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Ein Blick hinter die Kulissen unseres Lagers in Schenklengsfeld –
              ständig wechselnde Sonderposten, Restbestände und Markenware,
              direkt vom Hersteller zu Ihnen.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={EBAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pz-btn pz-btn-primary"
              >
                Im eBay-Shop entdecken <ArrowUpRight size={18} />
              </a>
              <button
                onClick={togglePlay}
                data-testid="video-toggle-play"
                className="pz-btn pz-btn-outline-gold"
              >
                {playing ? (
                  <>
                    <Pause size={18} /> Pause
                  </>
                ) : (
                  <>
                    <Play size={18} /> Abspielen
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="relative border border-white/15 overflow-hidden bg-black">
              <video
                ref={videoRef}
                data-testid="video-showcase-player"
                src="/video/prozenthaus.mp4"
                poster="/video/prozenthaus-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-auto block"
                aria-label="Prozenthaus – Eindrücke aus unserem Lager in Schenklengsfeld"
              />
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/55 backdrop-blur-sm px-3 py-1.5 border border-white/15">
                <span className="inline-block w-2 h-2 rounded-full bg-[var(--pz-accent)] animate-pulse" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white">
                  Aus dem Lager
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- Categories ----------
const Categories = () => (
  <section
    id="categories"
    data-testid={TESTIDS.categoriesSection}
    className="border-b border-[var(--pz-border)] bg-[var(--pz-surface-alt)]"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <div className="pz-eyebrow-gold mb-4">Unsere Warenbereiche</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
            Sechs Bereiche.
            <br />
            Ein <span className="pz-highlight">ständig wechselndes</span> Sortiment.
          </h2>
        </div>
        <a
          href={EBAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="pz-btn pz-btn-dark"
        >
          Alle Angebote auf eBay <ArrowUpRight size={18} />
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 lg:grid-rows-3 gap-4 lg:auto-rows-[200px]">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <a
              key={cat.slug}
              href={EBAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TESTIDS.categoryCard(cat.slug)}
              className={`pz-card relative overflow-hidden border border-[var(--pz-border)] bg-white block min-h-[220px] ${cat.span}`}
            >
              <div className="absolute inset-0 pz-img-zoom">
                <img
                  src={cat.image}
                  alt={`Sonderposten & Markenware – ${cat.title} im Prozenthaus`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-white/10" />
              </div>
              <div className="absolute top-4 left-4 bg-white p-2 border border-[var(--pz-border)]">
                <Icon size={18} className="text-[var(--pz-text)]" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-[var(--pz-accent-light)] text-[0.7rem] uppercase tracking-[0.24em] font-semibold">
                  {cat.short}
                </div>
                <div className="mt-1 font-display font-black text-white text-lg sm:text-xl leading-tight">
                  {cat.title}
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-white text-sm font-semibold">
                  Im Shop ansehen <ArrowUpRight size={16} />
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  </section>
);

// ---------- eBay Preview ----------
const EbayPreview = () => (
  <section
    id="ebay"
    data-testid={TESTIDS.ebayPreviewSection}
    className="border-b border-[var(--pz-border)]"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="pz-eyebrow-gold mb-4">Direkt online kaufen</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight">
            Unser komplettes Sortiment im
            <span className="pz-highlight"> eBay-Shop.</span>
          </h2>
          <p className="mt-6 text-[var(--pz-text-muted)] text-lg leading-relaxed max-w-xl">
            Stöbern Sie bequem von zu Hause aus durch unsere aktuellen Sonderposten
            und Restbestände. Bewertet, sicher und schnell versendet – mit dem
            vollen Käuferschutz von eBay.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={EBAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TESTIDS.ebayPreviewCta}
              className="pz-btn pz-btn-primary text-base"
            >
              <ShoppingBag size={18} /> Jetzt im eBay-Shop einkaufen
            </a>
            <a
              href={EBAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pz-btn pz-btn-outline-gold"
            >
              Bewertungen ansehen <ArrowUpRight size={18} />
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[var(--pz-border)] border border-[var(--pz-border)]">
            {[
              { l: "eBay-Käuferschutz", v: "100%" },
              { l: "Versand", v: "Schnell" },
              { l: "Rückgabe", v: "Möglich" },
            ].map((s, i) => (
              <div key={i} className="bg-white px-5 py-4">
                <div className="pz-eyebrow">{s.l}</div>
                <div className="font-display font-black text-2xl mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-[var(--pz-text)] bg-[var(--pz-text)] text-white p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-44 h-44 bg-[var(--pz-accent)]/15 rounded-full" />
            <div className="absolute -bottom-16 -left-10 w-40 h-40 border border-[var(--pz-accent)]/30 rounded-full" />
            <div className="pz-eyebrow text-white/60">ebay.de/str</div>
            <div className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">
              <span className="text-[var(--pz-accent)]">prozent</span>haus
            </div>
            <div className="mt-6 space-y-3 relative">
              {[
                "Aktuelle Sonderposten",
                "Tagesangebote & Restbestände",
                "Markenware bis 50% reduziert",
                "Bewertete & sichere Abwicklung",
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={18} className="text-[var(--pz-accent)]" />
                  <span className="text-sm">{t}</span>
                </div>
              ))}
            </div>
            <a
              href={EBAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pz-btn pz-btn-primary mt-8 w-full justify-center relative"
            >
              Shop öffnen <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Why Us ----------
const WhyUs = () => {
  const items = [
    {
      icon: Percent,
      t: "Bis zu 50% Rabatt",
      d: "Echte Preisvorteile auf hochwertige Markenartikel – ohne versteckte Kosten.",
    },
    {
      icon: RefreshCw,
      t: "Wechselndes Angebot",
      d: "Jede Woche neue Sonderposten und Restbestände – ein Besuch lohnt sich immer.",
    },
    {
      icon: BadgeCheck,
      t: "Geprüfte Markenware",
      d: "Wir setzen ausschließlich auf Originalware namhafter Hersteller.",
    },
    {
      icon: ShoppingBag,
      t: "Lokal & Online",
      d: "Direkt vor Ort in Schenklengsfeld oder bequem über unseren eBay-Shop.",
    },
  ];

  return (
    <section
      data-testid={TESTIDS.whyUsSection}
      className="border-b border-[var(--pz-border)] bg-[var(--pz-text)] text-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="pz-eyebrow-gold mb-4">Warum Prozenthaus</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
              Vier gute Gründe,
              <br />
              <span className="font-serif-italic font-medium text-[var(--pz-accent-light)]">bei uns</span> zu kaufen.
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/15 border border-white/15">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="bg-[var(--pz-text)] p-8">
                  <Icon className="text-[var(--pz-accent)]" size={28} />
                  <div className="mt-5 font-display font-black text-2xl">{it.t}</div>
                  <p className="mt-2 text-white/70 leading-relaxed text-sm">
                    {it.d}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const OpenStatusBadge = () => {
  const [status, setStatus] = useState(() => getOpenStatus());
  useEffect(() => {
    const tick = () => setStatus(getOpenStatus());
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);
  return (
    <span
      data-testid="open-status-badge"
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 ${
        status.open
          ? "bg-[var(--pz-accent-soft)] text-[var(--pz-accent-hover)] border border-[var(--pz-accent)]"
          : "bg-[var(--pz-surface-alt)] text-[var(--pz-text-muted)] border border-[var(--pz-border)]"
      }`}
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${
          status.open ? "bg-[var(--pz-accent)]" : "bg-[var(--pz-text-muted)]"
        }`}
      />
      {status.label}
    </span>
  );
};

// ---------- Contact ----------
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: "" });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        subject: form.subject || null,
        message: form.message,
      });
      setState({ loading: false, success: true, error: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Nachricht konnte nicht gesendet werden. Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: msg });
    }
  };

  return (
    <section
      id="contact"
      data-testid={TESTIDS.contactSection}
      className="border-b border-[var(--pz-border)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="pz-eyebrow-gold mb-4">Kontakt</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
              Sprechen Sie
              <br />
              <span className="font-serif-italic font-medium text-[var(--pz-accent)]">uns an.</span>
            </h2>
            <p className="mt-6 text-[var(--pz-text-muted)] leading-relaxed max-w-md">
              Sie haben Fragen zu einem Artikel, einer Bestellung oder
              Großmengen-Anfragen? Wir freuen uns auf Ihre Nachricht.
            </p>

            <div className="mt-10 space-y-5">
              <address className="not-italic space-y-5 block">
              <div className="flex items-start gap-4">
                <div className="p-2 border border-[var(--pz-border)] bg-white">
                  <MapPin size={18} className="text-[var(--pz-accent)]" />
                </div>
                <div>
                  <div className="pz-eyebrow">Adresse</div>
                  <div className="mt-1 font-semibold">Prozenthaus</div>
                  <div className="text-[var(--pz-text-muted)] text-sm">
                    Ron Kowalewsky
                    <br />
                    Landeckerstraße 20A
                    <br />
                    36277 Schenklengsfeld
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 border border-[var(--pz-border)] bg-white">
                  <Phone size={18} className="text-[var(--pz-accent)]" />
                </div>
                <div>
                  <div className="pz-eyebrow">Telefon</div>
                  <a
                    href="tel:+4966292160485"
                    className="mt-1 block font-semibold pz-link"
                  >
                    06629 2160485
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 border border-[var(--pz-border)] bg-white">
                  <Mail size={18} className="text-[var(--pz-accent)]" />
                </div>
                <div>
                  <div className="pz-eyebrow">E-Mail</div>
                  <a
                    href="mailto:team@prozenthaus.shop"
                    className="mt-1 block font-semibold pz-link"
                  >
                    team@prozenthaus.shop
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 border border-[var(--pz-border)] bg-white">
                  <Clock size={18} className="text-[var(--pz-accent)]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <div className="pz-eyebrow">Öffnungszeiten</div>
                    <OpenStatusBadge />
                  </div>
                  <dl
                    data-testid="opening-hours-list"
                    className="mt-2 text-sm space-y-1"
                  >
                    {OPENING_HOURS_DISPLAY.map((row) => (
                      <div
                        key={row.range}
                        className="flex justify-between gap-4"
                      >
                        <dt className="text-[var(--pz-text-muted)]">
                          {row.range}
                        </dt>
                        <dd
                          className={
                            row.hours === "Geschlossen"
                              ? "text-[var(--pz-text-muted)]"
                              : "font-semibold"
                          }
                        >
                          {row.hours}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
              </address>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[var(--pz-border)] p-6 sm:p-8 space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="pz-eyebrow block mb-2">Name *</label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    data-testid={TESTIDS.contactName}
                    placeholder="Ihr Name"
                    className="pz-input"
                  />
                </div>
                <div>
                  <label className="pz-eyebrow block mb-2">E-Mail *</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    data-testid={TESTIDS.contactEmail}
                    placeholder="name@beispiel.de"
                    className="pz-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="pz-eyebrow block mb-2">Telefon</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid={TESTIDS.contactPhone}
                    placeholder="Optional"
                    className="pz-input"
                  />
                </div>
                <div>
                  <label className="pz-eyebrow block mb-2">Betreff</label>
                  <input
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    data-testid={TESTIDS.contactSubject}
                    placeholder="Worum geht es?"
                    className="pz-input"
                  />
                </div>
              </div>

              <div>
                <label className="pz-eyebrow block mb-2">Nachricht *</label>
                <textarea
                  required
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  data-testid={TESTIDS.contactMessage}
                  placeholder="Wie können wir helfen?"
                  className="pz-input resize-none"
                />
              </div>

              {state.success && (
                <div
                  data-testid={TESTIDS.contactSuccess}
                  className="flex items-center gap-3 border border-[var(--pz-accent)] bg-[var(--pz-accent-soft)] text-[var(--pz-text)] px-4 py-3"
                >
                  <CheckCircle2 size={18} className="text-[var(--pz-accent)]" />
                  <span className="text-sm font-semibold">
                    Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.
                  </span>
                </div>
              )}
              {state.error && (
                <div
                  data-testid={TESTIDS.contactError}
                  className="flex items-center gap-3 border border-red-700 bg-red-50 text-red-800 px-4 py-3"
                >
                  <AlertCircle size={18} />
                  <span className="text-sm font-semibold">{state.error}</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-[var(--pz-text-muted)]">
                  Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Anfrage gemäß
                  unserer{" "}
                  <a href="/datenschutz" className="pz-link">
                    Datenschutzerklärung
                  </a>{" "}
                  zu.
                </p>
                <button
                  type="submit"
                  disabled={state.loading}
                  data-testid={TESTIDS.contactSubmit}
                  className="pz-btn pz-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {state.loading ? "Wird gesendet..." : "Nachricht senden"}{" "}
                  <ArrowRight size={18} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const location = useLocation();
  useSeo({
    title: "Prozenthaus – Markenware bis 50 % reduziert | Schenklengsfeld",
    description:
      "Markenware bis 50 % reduziert im Prozenthaus Schenklengsfeld. Lagerrestbestände, Überschusswaren & Sonderposten in 6 Bereichen – Baumarkt, Haushalt, IT, Multimedia, Camping & Heimwerken.",
    canonical: "https://prozenthaus.shop/",
  });

  // Handle deep-links from other pages (e.g. /#contact)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    }
  }, [location]);

  return (
    <>
      <Hero />
      <About />
      <VideoShowcase />
      <Categories />
      <EbayProducts />
      <EbayPreview />
      <WhyUs />
      <Contact />
    </>
  );
};

export default Home;
